import { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronRight,
  KeyRound,
  LogOut,
  Moon,
  Sun,
} from 'lucide-react';

import { useAuth } from '@/shared/auth';
import { Theme } from '@/shared/config';
import { cn, useTheme } from '@/shared/libs';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui';
import { ROLE_LABEL_KEY } from '@entities/User';
import { useNavigate } from '@tanstack/react-router';

import { ChangePasswordModal } from '../ChangePasswordModal/ChangePasswordModal';
import { LanguageSwitcher } from '../LanguageSwitcher/LanguageSwitcher';

export interface UserMenuProps {
  variant?: 'header' | 'sidebar';
}

export const UserMenu: FC<UserMenuProps> = ({ variant = 'header' }) => {
  const { t } = useTranslation();
  const { changeTheme, theme } = useTheme();
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [passwordOpen, setPasswordOpen] = useState(false);

  const roleLabel = user?.role
    ? t(ROLE_LABEL_KEY[user.role])
    : t('roles.pending');
  const userInitial = (user?.email ?? '?').charAt(0).toUpperCase();
  const isSidebar = variant === 'sidebar';

  const handleToggleTheme = useCallback(() => {
    changeTheme(theme === Theme.Dark ? Theme.Light : Theme.Dark);
  }, [changeTheme, theme]);

  const handleLogout = useCallback(async (): Promise<void> => {
    await logout(() => {
      void navigate({ to: '/login' });
    });
  }, [logout, navigate]);

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          {isSidebar ? (
            <button
              className="flex w-full cursor-pointer items-center gap-3 rounded-xl bg-foreground/5 px-3.5 py-2 text-left transition-colors hover:bg-foreground/10"
              title={t('buttons.profile')}
              type="button">
              <div className="brand-gradient flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-brand-foreground">
                {userInitial}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">
                  {user?.email ?? t('roles.pending')}
                </p>
                <p className="text-[10px] text-muted-foreground">{roleLabel}</p>
              </div>
              <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
            </button>
          ) : (
            <button
              className="flex cursor-pointer items-center gap-1.5 rounded-full p-1 pr-1.5 transition-colors hover:bg-foreground/5"
              title={t('buttons.profile')}
              type="button">
              <div className="brand-gradient flex size-8 items-center justify-center rounded-full text-xs font-semibold text-brand-foreground">
                {userInitial}
              </div>
              <ChevronDown className="size-3.5 text-muted-foreground" />
            </button>
          )}
        </PopoverTrigger>

        <PopoverContent
          align="end"
          avoidCollisions={!isSidebar}
          className={cn(
            'rounded-2xl border-border/60 bg-card shadow-xl',
            isSidebar ? 'w-56 p-1.5' : 'w-60 p-2',
          )}
          side={isSidebar ? 'right' : 'bottom'}
          sideOffset={isSidebar ? 12 : 4}>
          {!!user && !isSidebar && (
            <div className="mb-1 flex items-center gap-3 px-2.5 py-2">
              <div className="brand-gradient flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-brand-foreground">
                {userInitial}
              </div>
              <div className="min-w-0">
                <p className="truncate text-xs font-medium">{user.email}</p>
                <p className="text-[10px] text-muted-foreground">{roleLabel}</p>
              </div>
            </div>
          )}

          {!isSidebar && (
            <>
              <div className="my-1 h-px bg-border/40" />

              <div className="px-1 py-1.5">
                <LanguageSwitcher />
              </div>
            </>
          )}

          <button
            className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
            type="button"
            onClick={() => setPasswordOpen(true)}>
            <KeyRound className="size-4 shrink-0" />
            {t('buttons.changePassword')}
          </button>

          {!isSidebar && (
            <button
              className="flex w-full cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
              type="button"
              onClick={handleToggleTheme}>
              <span className="flex items-center gap-2.5">
                {theme === Theme.Dark ? (
                  <Sun className="size-4" />
                ) : (
                  <Moon className="size-4" />
                )}
                {theme === Theme.Dark ? t('theme.light') : t('theme.dark')}
              </span>
            </button>
          )}

          <button
            className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/5 hover:text-destructive"
            type="button"
            onClick={() => void handleLogout()}>
            <LogOut className="size-4 shrink-0" />
            {t('buttons.logout')}
          </button>
        </PopoverContent>
      </Popover>

      {passwordOpen && (
        <ChangePasswordModal onClose={() => setPasswordOpen(false)} />
      )}
    </>
  );
};

UserMenu.displayName = 'UserMenu';
